import React from 'react';
import { Supplier } from '../../services/supplierFinderService';

interface SupplierResultsProps {
  suppliers: Supplier[];
  isLoading: boolean;
  error: string | null;
}

const SupplierResults: React.FC<SupplierResultsProps> = ({ suppliers, isLoading, error }) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Finding suppliers...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }
  
  if (suppliers.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <p className="text-gray-600">Use the form above to search for suppliers</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-4">Potential Suppliers</h2>
      
      {suppliers.map((supplier, index) => (
        <div 
          key={index}
          className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
        >
          <div className="border-l-4 border-green-500 pl-4 py-4 pr-6">
            <h3 className="text-xl font-semibold text-gray-800">{supplier.name}</h3>
            
            <div className="mt-2 text-gray-600">
              <p>{supplier.description}</p>
            </div>
            
            <div className="mt-4 flex flex-wrap gap-y-2 gap-x-6">
              {supplier.location && (
                <div className="flex items-center text-sm">
                  <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{supplier.location}</span>
                </div>
              )}
              
              {supplier.contactInfo && (
                <div className="flex items-center text-sm">
                  <svg className="w-4 h-4 mr-1 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>{supplier.contactInfo}</span>
                </div>
              )}
            </div>
            
            {supplier.website && (
              <div className="mt-4">
                <a 
                  href={supplier.website.startsWith('http') ? supplier.website : `https://${supplier.website}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-1 text-sm font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200 transition-colors"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Visit Website
                </a>
              </div>
            )}
          </div>
        </div>
      ))}
      
      <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-sm text-gray-600">
          <span className="font-medium">Note:</span> The information provided is based on our AI search and may not be comprehensive. 
          We recommend conducting your own due diligence before establishing business relationships with these suppliers.
        </p>
      </div>
    </div>
  );
};

export default SupplierResults;
